import React, { useEffect, useState, useRef } from 'react';
import { useClickOutside } from '@src/hooks/useClickOutSide';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import SearchIcon from '@mui/icons-material/Search';
import {
  Popover,
  InputBase,
  Box,
  CircularProgress,
  Stack,
} from '@mui/material';
import Tooltip from '@src/components/Tooltip';
import { COLOR } from '@src/styles/color';
import Typography from '../Typography';
import { variantMapping } from './variants';
import {
  PopoverMenuItem,
  StyledDropdown,
  StyledListMenuItem,
} from './index.style';

const DropdownCustom = ({
  options,
  startIcon,
  value,
  title,
  onChange,
  variant,
  disabled,
  tooltipContent,
  content,
  contentEmpty,
  searchable = false,
  isSelectingOption = false,
  searchPlaceholder,
  stopClickOutside = false,
  widthContentEmpty,
  loading,
  open,
  onClose,
  onOpen,
  width,
}) => {
  const [anchor, setAnchor] = useState(null);
  const [openTooltip, setOpenTooltip] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(open || false);
  const [search, setSearch] = useState('');
  const [showScrollbar, setShowScrollbar] = useState(false);

  const menuContainerRef = useRef(null);

  const popperContainerRef = useClickOutside(
    () => {
      setSearch('');
      setOpenDropdown(false);
      setAnchor(null);
      setOpenTooltip(false);
      setShowScrollbar(false);
      onClose?.();
    },
    ['mousedown'],
    null,
    stopClickOutside,
  );

  const handleDropdownClick = (event) => {
    if (disabled) return;
    setOpenDropdown(true);
    setAnchor(event.currentTarget);
    setOpenTooltip(false);
    onOpen?.();
  };

  useEffect(() => {
    setOpenDropdown(open);
  }, [open]);

  const renderArrow = (anchorDropdown) => {
    if (anchorDropdown) {
      return (
        <ArrowDropUpRoundedIcon
          sx={{ color: COLOR.navyBlue, fontSize: '24px' }}
        />
      );
    }
    return (
      <ArrowDropDownRoundedIcon
        sx={{ color: COLOR.navyBlue, fontSize: '24px' }}
      />
    );
  };

  const filteredOptions = search
    ? options.filter((option) =>
        option.label.toLowerCase().includes(search.toLowerCase()),
      )
    : options;

  const selectedIndex = filteredOptions.findIndex(
    (item) => item.value === value,
  );

  useEffect(() => {
    if (!anchor || !selectedIndex || !searchable) return;

    setTimeout(() => {
      if (!menuContainerRef.current) return;
      const selectedItem = menuContainerRef.current.querySelector(
        `.item-${selectedIndex}`,
      );
      if (selectedItem && menuContainerRef.current) {
        const container = menuContainerRef.current;
        const itemTop = selectedItem.offsetTop;
        const itemHeight = selectedItem.offsetHeight;
        const containerHeight = container.clientHeight;

        container.scrollTop = itemTop - containerHeight / 2 + itemHeight / 2;
      }
    }, 0);
  }, [anchor, selectedIndex, searchable]);

  const getPopoverWidth = () => {
    if (widthContentEmpty) return widthContentEmpty;
    const variantWidth = variantMapping[variant]?.widthPapers;
    if (variantWidth === '100%') {
      return anchor?.offsetWidth;
    }
    return variantWidth;
  };

  return (
    <Tooltip
      arrow
      title={tooltipContent}
      placement="top"
      disableHoverListener={!tooltipContent}
      disableFocusListener={!tooltipContent}
      open={openTooltip && tooltipContent}
    >
      <StyledDropdown
        metaData={variantMapping[variant]}
        isSelectingOption={isSelectingOption}
        width={width}
      >
        <div
          onClick={handleDropdownClick}
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
          className={`dropdown-container ${disabled ? 'disabled' : ''}`}
          onMouseEnter={() => setOpenTooltip(true)}
          onMouseLeave={() => setOpenTooltip(false)}
        >
          <div className="dropdown-title">
            {startIcon}
            {title}
          </div>
          {renderArrow(anchor)}
        </div>
        <Popover
          open={openDropdown}
          anchorEl={anchor}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          className="popover-date-range-picker"
          PaperProps={{
            style: {
              borderRadius: '8px',
              width: getPopoverWidth(),
            },
            sx: {
              marginTop: variantMapping[variant]?.marginTopPopover,
              boxShadow: '0px 4px 16px 0px #00000014 !important',
            },
          }}
        >
          <div ref={popperContainerRef}>
            {searchable && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  background: 'transparent',
                  height: '40px',
                  padding: '8px 16px 0px 16px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1,
                  backgroundColor: COLOR.white,
                }}
              >
                <SearchIcon
                  sx={{ color: COLOR.navyBlue, mr: 1, fontSize: '20px' }}
                />
                <InputBase
                  value={search}
                  autoFocus
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={searchPlaceholder}
                  sx={{
                    fontSize: '14px',
                    color: COLOR.darkGray,
                    width: '100%',
                    background: 'transparent',
                  }}
                  inputProps={{
                    'aria-label': searchPlaceholder,
                  }}
                />
                {search && (
                  <CloseRoundedIcon
                    onClick={() => setSearch('')}
                    sx={{ color: COLOR.navyBlue, fontSize: '20px' }}
                    style={{ cursor: 'pointer' }}
                  />
                )}
              </Box>
            )}
            <StyledListMenuItem
              ref={menuContainerRef}
              onMouseEnter={() => setShowScrollbar(true)}
              onMouseLeave={() => setShowScrollbar(false)}
              metaData={variantMapping[variant]}
              overflow={Boolean(filteredOptions?.length > 5)}
              className={`dropdown__menu_items ${
                showScrollbar ? 'scrollbar-custom' : 'hidden-scrollbar'
              }`}
              searchable={searchable}
            >
              {loading && (
                <Stack spacing={2} direction="row" justifyContent="center">
                  <CircularProgress
                    size={24}
                    sx={{
                      color: COLOR.navyBlue,
                      textAlign: 'center',
                    }}
                  />
                </Stack>
              )}
              {!loading && filteredOptions?.length > 0
                ? filteredOptions.map((option, idx) => (
                    <PopoverMenuItem
                      metaData={variantMapping[variant]}
                      onClick={() => {
                        if (option?.disabled) return;
                        setOpenDropdown(false);
                        onClose?.();
                        setAnchor(null);
                        setSearch('');
                        onChange(option.value);
                      }}
                      key={option.value}
                      style={{
                        backgroundColor:
                          option.value === value &&
                          COLOR.backgroundLinearProgress,
                        borderRadius: '8px',
                        height:
                          variantMapping[variant]?.itemHeight ||
                          variantMapping[variant]?.height,
                        padding: variantMapping[variant]?.paddingListMenuItem,
                      }}
                      className={`dropdown__menu_item item-${idx}${
                        option.value === value ? ' selected' : ''
                      }${option?.disabled ? ' disabled' : ''}`}
                    >
                      <Typography
                        variant="body-14-medium"
                        color={COLOR.darkGray}
                      >
                        {option?.labelCustom || option.label}
                      </Typography>
                    </PopoverMenuItem>
                  ))
                : !loading && <>{contentEmpty}</>}
            </StyledListMenuItem>
            {content && <div>{content}</div>}
          </div>
        </Popover>
      </StyledDropdown>
    </Tooltip>
  );
};

export default DropdownCustom;
